import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from "@angular/core";
import { authenticationService } from "@app/login/auth.service";
import { ToastrService } from "ngx-toastr";
import { Permissions } from "@models/permissions.enum";
import * as XLSX from "xlsx";
import { FuelService } from "@app/fuel/fuel.service";
import { RatesService } from "@app/rates/shared/rates.service";
import { GasRegion } from "@app/shared/models/fuel/GasRegion.model";
import { BoundedTableComponent } from "../../bounded-table/bounded-table.component";
import { FuelSurchargeViewModel } from "@app/shared/models/rates/FuelSurchargeViewModel.model";
import { ExcelExportService } from "@app/shared/services/excel-export.service";

@Component({
  selector: "app-edit-fuel-surcharges",
  templateUrl: "./edit-fuel-surcharges.component.html",
  styleUrls: ["./edit-fuel-surcharges.component.scss"],
})
export class EditFuelSurchargesComponent implements OnInit {
  @ViewChild("clientRatesTable", { static: true }) clientRatesTable: BoundedTableComponent;
  @ViewChild("driverRatesTable", { static: true }) driverRatesTable: BoundedTableComponent;

  @Input() rate: FuelSurchargeViewModel;
  @Input() rateId: number;
  @Input() isEditing: boolean;
  @Input() isCreating: boolean;
  // regions
  @Input() fuelRegions: GasRegion[] = [];
  @Output() fuelRegionsChange = new EventEmitter();
  @Input() selectedRegions: GasRegion[] = [];
  @Output() selectedRegionsChange = new EventEmitter();

  // dropdown settings
  originSettings: any;
  destSettings: any;

  // table columns
  firstColumnString: string = "PRICE";
  columns: { field: string; name: string; type: string }[];

  driverFirstColumnString: string = "PRICE";
  driverColumns: { field: string; name: string; type: string }[];

  // rate by
  rateBy: string = "PRICE";
  ratingType: string = "WEIGHT";

  clientRates: any[] = [];
  driverRates: any[] = [];
  linkedDriverRates: any[] = [];

  decimals: number = 2;
  stopToUse: string = "LOAD";
  effectiveDate: number = 0;
  clientPriceIncrement: number | string;
  clientPriceInterval: number | string;
  driverPriceIncrement: number | string;
  driverPriceInterval: number | string;
  linkedDriverPriceIncrement: number | string;
  linkedDriverPriceInterval: number | string;

  driverFuelSurchargeRadio: string = "CREATE";

  driverFuelSurcharges: any[] = [];
  selectedDriverFuelSurcharge: any;

  constructor(
    private toastr: ToastrService,
    private fuelService: FuelService,
    private excelService: ExcelExportService,
    private ratesService: RatesService,
    private authService: authenticationService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.columns = [
      {
        field: "fuelSurchargePerMile",
        name: "FUEL SURCHARGE PER MILE ($)",
        type: "dollar",
      },
    ];

    this.driverColumns = [
      {
        field: "fuelSurchargePerMile",
        name: "FUEL SURCHARGE PER MILE ($)",
        type: "dollar",
      },
    ];

    this.fuelService.getRegions().subscribe((regions) => {
      // this.fuelRegions = regions;
      // filter out all of the candian cities for now
      const canadaNational = regions.find((region) => region.name === "CA - NATIONAL");
      this.fuelRegions = regions.filter((region) => region.gasRegionId !== canadaNational.id);
      this.fuelRegionsChange.emit(this.fuelRegions);
      if (!this.isCreating) {
        this.loadRates(this.rate);
      }
    });

    this.ratesService.getDriverFuelSurcharges().subscribe((surcharges) => {
      this.driverFuelSurcharges = surcharges;
      if (this.rate.driverFuelSurchargeId) {
        this.selectedDriverFuelSurcharge = this.driverFuelSurcharges.find(
          (surcharge) => surcharge.id === this.rate.driverFuelSurchargeId
        );
      }
      this.loadLinkedDriverFuelSurcharge(this.rate.driverFuelSurchargeId);
    });
    if (this.rate.linkDriverFuelSurcharge) {
      this.driverFuelSurchargeRadio = "LINK";
    } else if (this.rate.isStraightPassThrough) {
      this.driverFuelSurchargeRadio = "PASSTHROUGH";
    } else {
      this.driverFuelSurchargeRadio = "CREATE";
    }
  }

  hasPermission(permissionName: string) {
    const permission = Permissions[permissionName];
    return this.authService.hasPermission(permission);
  }

  rateByChanged(type: string): void {
    // update column header
    /*this.columns = [
      {
        field: "fuelSurchargePerMile",
        name: "Fuel Surcharge Per Mile " + (type === "PRICE" ? "($)" : "(0.02 = 2%)"),
        type: type === "PRICE" ? "dollar" : "percentage",
      },
    ];*/
    if (type === "PRICE") {
      this.columns = [
        {
          field: "fuelSurchargePerMile",
          name: "Fuel Surcharge Per Mile ($)",
          type: "dollar",
        },
      ];
      this.firstColumnString = "PRICE";
      if (this.driverFuelSurchargeRadio === "CREATE") {
        this.driverColumns = [
          {
            field: "fuelSurchargePerMile",
            name: "Fuel Surcharge Per Mile ($)",
            type: "dollar",
          },
        ];
        this.driverFirstColumnString = "PRICE";
      }
    } else if (type === "PERCENTAGE") {
      this.columns = [
        {
          field: "fuelSurchargePerMile",
          name: "Fuel Surcharge Per Mile (0.02 = 2%)",
          type: "percentage",
        },
      ];
      this.firstColumnString = "PERCENTAGE";
      if (this.driverFuelSurchargeRadio === "CREATE") {
        this.driverColumns = [
          {
            field: "fuelSurchargePerMile",
            name: "Fuel Surcharge Per Mile (0.02 = 2%)",
            type: "percentage",
          },
        ];
        this.driverFirstColumnString = "PERCENTAGE";
      }
    } else if (type === "DATERANGE") {
      this.columns = [
        {
          field: "fuelSurchargePerMile",
          name: "Fuel Surcharge Per Mile (0.02 = 2%)",
          type: "daterange",
        },
      ];
      this.firstColumnString = "DATERANGE";
      if (this.driverFuelSurchargeRadio === "CREATE") {
        this.driverColumns = [
          {
            field: "fuelSurchargePerMile",
            name: "Fuel Surcharge Per Mile (0.02 = 2%)",
            type: "daterange",
          },
        ];
        this.driverFirstColumnString = "DATERANGE";
      }
    }
  }

  driverFuelSurchargeChanged(type: string): void {
    this.driverFuelSurchargeRadio = type;
    if (type === "LINK") {
      this.rate.isStraightPassThrough = false;
      this.rate.linkDriverFuelSurcharge = true;
      this.driverRates = [];
      this.driverRatesTable.updatedRows();
    } else if (type === "CREATE") {
      this.rate.isStraightPassThrough = false;
      this.rate.linkDriverFuelSurcharge = false;
    } else if (type === "PASSTHROUGH") {
      this.rate.isStraightPassThrough = true;
      this.rate.linkDriverFuelSurcharge = false;
      this.driverRates = [];
      this.driverRatesTable.updatedRows();
    }
  }

  validate = () => {
    const warnings = [];
    if (!this.rate.name) warnings.push("Rate Name is required");
    if (!this.rate.chargeTypeId) warnings.push("Charge Type is required");
    if (this.rateBy === "PERCENTAGE") {
      for (const rate of this.clientRates) {
        if (rate.values.fuelSurchargePerMile >= 1) {
          warnings.push("Percentages must be in the form 0.02 = 2%");
        }
      }
    }
    if (!this.clientRates.length) warnings.push("Rates are required");
    if (!this.clientRatesTable.validateRanges())
      warnings.push(`Price Ranges are invalid - each row must differ by 0.001`);
    if (!this.rate.isStraightPassThrough) {
      if (!this.rate.linkDriverFuelSurcharge) {
        if (!this.driverRates.length) warnings.push("Driver Rates are required");
        if (!this.driverRatesTable.validateRanges())
          warnings.push(`Driver Price Ranges are invalid - each row must differ by 0.001`);
      } else {
        if (!this.selectedDriverFuelSurcharge) {
          warnings.push("Driver Fuel Surcharge is required");
        }
      }
    }

    if(this.rate.effectiveDate < 0) {
      warnings.push("Effective Date cannot be negative");
    }

    if (warnings.length > 0) {
      for (const warning of warnings) {
        this.toastr.warning(warning);
      }
      return false;
    }
    return true;
  };

  filterObject(object: any, names: string[], defaultValue: any) {
    const oldValues = object;
    let newValues = {};
    for (let name of names) {
      newValues[name] = oldValues[name] || defaultValue;
    }
    return newValues;
  }

  roundToDecimals(value: any) {
    return Number(value).toFixed(this.decimals);
  }

  mapRatesToTable(rates: any[]) {
    let rows = [];
    let bounds = {};

    // create the row objects
    for (let item of rates) {
      const { floor, roof, dateFloor, dateRoof, kind, price } = item;
      const boundsKey = `${floor}-${roof}`;

      if (!bounds[boundsKey]) {
        bounds[boundsKey] = { floor, roof, dateFloor, dateRoof, values: {} };
      }

      bounds[boundsKey].values["fuelSurchargePerMile"] = price;
    }

    rows = Object.values(bounds);
    rows = rows.sort((row1, row2) => row1.floor - row2.floor);

    return rows;
  }

  loadRates(surcharge: FuelSurchargeViewModel) {
    this.rateBy = surcharge.rateBy;
    this.rateByChanged(this.rateBy);
    this.ratingType = surcharge.ratingType;

    this.decimals = surcharge.decimals;
    this.stopToUse = surcharge.stopToUse;
    this.effectiveDate = surcharge.effectiveDate ? surcharge.effectiveDate : 0;
    this.clientPriceIncrement = surcharge.clientPriceIncrement;
    this.clientPriceInterval = surcharge.clientPriceInterval;
    this.driverPriceIncrement = surcharge.driverPriceIncrement;
    this.driverPriceInterval = surcharge.driverPriceInterval;

    if (surcharge.gasRegions) {
      const gasRegionIds = surcharge.gasRegions.map((region) => region.id);

      this.selectedRegions = this.fuelRegions.filter((region) => gasRegionIds.includes(region.id));
      this.selectedRegionsChange.emit(this.selectedRegions);
    }

    // decimals need to be initialized before doing this mapping
    this.clientRates = this.mapRatesToTable(
      surcharge.fuelSurchargeRates.filter((item) => item.kind === "CLIENT")
    );
    this.driverRates = this.mapRatesToTable(
      surcharge.fuelSurchargeRates.filter((item) => item.kind === "DRIVER")
    );
  }

  loadLinkedDriverFuelSurcharge(id: any) {
    if (id) {
      this.ratesService.getDriverFuelSurchargesById(id).subscribe((surcharge) => {
        this.linkedDriverRates = this.mapRatesToTable(surcharge.fuelSurchargeRates);
        this.linkedDriverPriceIncrement = surcharge.driverPriceIncrement;
        this.linkedDriverPriceInterval = surcharge.driverPriceInterval;
        if (surcharge.rateBy === "PRICE") {
          this.driverColumns = [
            {
              field: "fuelSurchargePerMile",
              name: "Fuel Surcharge Per Mile ($)",
              type: "dollar",
            },
          ];
          this.driverFirstColumnString = "PRICE";
        } else if (surcharge.rateBy === "PERCENTAGE") {
          this.driverColumns = [
            {
              field: "fuelSurchargePerMile",
              name: "Fuel Surcharge Per Mile (0.02 = 2%)",
              type: "percentage",
            },
          ];
          this.driverFirstColumnString = "PERCENTAGE";
        } else if (surcharge.rateBy === "DATERANGE") {
          this.driverColumns = [
            {
              field: "fuelSurchargePerMile",
              name: "Fuel Surcharge Per Mile (0.02 = 2%)",
              type: "daterange",
            },
          ];
          this.driverFirstColumnString = "DATERANGE";
        }
        this.driverRatesTable.updatedRows();
        this.cdr.detectChanges();
      });
    }
  }

  //For parsing this page's data into a request object
  buildRatesObject = () => {
    const buildRatesFromRows = (rows: any[], type: any) => {
      // map from the rates table to individual rates items
      let rates = [];
      const kind = type;
      for (const row of rows) {
        const { floor, roof, dateFloor, dateRoof, values } = row;
        const dateFloorParsed = new Date(dateFloor);
        const dateRoofParsed = new Date(dateRoof);
        for (const [name, price] of Object.entries(values)) {
          const rate = {
            floor,
            roof,
            dateFloor: dateFloorParsed,
            dateRoof: dateRoofParsed,
            price,
            kind,
          };
          rates.push(rate);
        }
      }
      return rates;
    };

    const clientFuelSurchargeRates = buildRatesFromRows(this.clientRates, "CLIENT");
    const driverFuelSurchargeRates = buildRatesFromRows(this.driverRates, "DRIVER");
    const fuelSurchargeRates = clientFuelSurchargeRates.concat(driverFuelSurchargeRates);

    //get everything from this.rate except driverFuelSurcharge
    const { driverFuelSurcharge, ...rateMinusDriverFuelSurcharge } = this.rate;

    const rateObject = {
      ...rateMinusDriverFuelSurcharge,
      clients: this.rate.clients.map((client) => ({ id: client.id })),
      chargeTypeId: this.rate.chargeTypeId,

      rateBy: this.rateBy.toUpperCase(),
      ratingType: this.ratingType.toUpperCase(),

      clientPriceIncrement: this.clientPriceIncrement,
      clientPriceInterval: this.clientPriceInterval,
      driverPriceIncrement: this.driverPriceIncrement,
      driverPriceInterval: this.driverPriceInterval,
      decimals: this.decimals,
      stopToUse: this.stopToUse,
      effectiveDate: this.effectiveDate,

      gasRegions: this.selectedRegions.map((region) => ({ id: region.id })),

      fuelSurchargeRates,
      driverFuelSurchargeId: this.selectedDriverFuelSurcharge?.id,
    };

    return rateObject;
  };

  exportExcel(rows: any[], cols: any[], title: string) {
    if (rows === undefined || cols === undefined) {
      this.toastr.warning("Table is empty.", "Unable to Export", {
        closeButton: true,
        enableHtml: true,
      });
      return;
    }

    //Get readable column headers
    const exportRows = [];
    rows.forEach((row) => {
      let thisExportRow = {} as any;
      if (row.floor != undefined && row.roof != undefined) {
        thisExportRow.PRICE = row.floor + "-" + row.roof;
      }

      if (row.values != undefined) {
        cols.forEach((col) => {
          thisExportRow[col.name.toUpperCase()] = row.values[col.field];
        });
      } else {
        thisExportRow = row;
      }

      exportRows.push(thisExportRow);
    });

    if (exportRows.length > 0) {
      this.excelService.exportAsExcelFile(exportRows, title);
    } else {
      this.toastr.warning("Table is empty.", "Unable to Export", {
        closeButton: true,
        enableHtml: true,
      });
    }
  }

  importExcel(event: any, buildFunction, cols: any[], table: any, fileUpload: any) {
    let reader = new FileReader();
    reader.readAsArrayBuffer(event.files[0]);
    reader.onload = (e) => {
      let data = new Uint8Array(reader.result as ArrayBuffer);
      let workbook = XLSX.read(data, { type: "array" });
      let sheet = workbook.Sheets[workbook.SheetNames[0]];
      let result = XLSX.utils.sheet_to_json(sheet);

      let exit = false;

      //Check that columns match
      cols.forEach((col) => {
        if (exit) {
          return;
        }
        result.forEach((resultRow: any[]) => {
          if (exit) {
            return;
          }
          if (resultRow[col.name] === undefined) {
            this.toastr.warning("Columns do not match this table.", "Unable to Import", {
              closeButton: true,
              enableHtml: true,
            });
            exit = true;
            return;
          }
        });
      });
      if (exit) {
        fileUpload.clear();
        return;
      }

      //Add to table
      try {
        let formattedResult = buildFunction(result);
        table.rows = this.mapRatesToTable(formattedResult);
        table.updatedRows();

        this.toastr.success("Columns successfully imported.", "Success", {
          closeButton: true,
          enableHtml: true,
        });
      } catch (error) {
        this.toastr.warning("Columns do not match this table.", "Unable to Import", {
          closeButton: true,
          enableHtml: true,
        });
      }

      fileUpload.clear();
    };
  }

  loadBoundedExcelData(data: any[]) {
    let rates = [];
    for (const row of data) {
      const price = row["PRICE"].split("-");
      const [floor, roof] = price;
      delete row["PRICE"];

      for (const [kind, price] of Object.entries(row)) {
        const rate = { floor, roof, kind, price };
        rates.push(rate);
      }
    }

    return rates;
  }
}
